<template>
  <div
    v-if="bannerHtml && (visible || adsensePending)"
    class="pw-popup-ad"
    :class="{
      'pw-popup-ad--pending': adsensePending,
      'pw-popup-ad--adsense': isAdsense
    }"
    role="dialog"
    aria-modal="true"
    aria-label="Advertisement"
  >
    <div class="pw-popup-ad__backdrop" aria-hidden="true" @click="closeBanner"></div>
    <div class="pw-popup-ad__dialog">
      <div class="pw-popup-ad__header">
        <button
          type="button"
          class="pw-popup-ad__close"
          aria-label="Close"
          @click.stop="closeBanner"
        >&times;</button>
      </div>
      <div ref="bannerContent" class="pw-popup-ad__content" v-html="bannerHtml"></div>
    </div>
  </div>
</template>

<script>
import { resolveWatchoutApiRoot } from '@/utils'
import {
  ADSENSE_POPUP_WAIT_MS,
  adsenseHasVisibleCreative,
  ensureAdsenseReady,
  isAdsenseBanner,
  requestAdsenseFill
} from '@/adsense'

const SESSION_KEY = 'plants4petsPopupAdSeen'
const API_ROOT = resolveWatchoutApiRoot()

function popupAdSeenThisSession () {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1'
  } catch (error) {
    return false
  }
}

function markPopupAdSeenThisSession () {
  try {
    sessionStorage.setItem(SESSION_KEY, '1')
  } catch (error) {}
}

export default {
  data () {
    return {
      visible: false,
      bannerHtml: '',
      isAdsense: false,
      adsensePending: false,
      adsenseObserver: null,
      adsenseTimer: 0,
      previewMode: false,
      adsenseStarted: false
    }
  },
  beforeDestroy () {
    this.stopAdsenseWatch()
  },
  mounted () {
    const params = new URLSearchParams(window.location.search)
    const requestedBannerId = params.get('banner')
    this.previewMode = Boolean(requestedBannerId)
    if (!requestedBannerId && popupAdSeenThisSession()) {
      return
    }
    const requestURL = new URL(API_ROOT + 'api/banner')
    requestURL.searchParams.set('product', 'plants4pets')
    if (requestedBannerId) {
      requestURL.searchParams.set('banner', requestedBannerId)
    }
    fetch(requestURL.toString())
      .then((response) => response.ok ? response.json() : null)
      .then((banner) => {
        if (!banner || !banner.id || !banner.html) {
          return
        }
        if (!requestedBannerId && popupAdSeenThisSession()) {
          return
        }
        this.bannerHtml = banner.html
        this.isAdsense = isAdsenseBanner(banner)
        if (this.isAdsense) {
          this.adsensePending = true
          this.$nextTick(() => this.waitForAdsenseFill())
          return
        }
        this.showBanner()
      })
      .catch(() => {})
  },
  methods: {
    stopAdsenseWatch () {
      if (this.adsenseObserver) {
        this.adsenseObserver.disconnect()
        this.adsenseObserver = null
      }
      if (this.adsenseTimer) {
        window.clearTimeout(this.adsenseTimer)
        this.adsenseTimer = 0
      }
    },
    showBanner () {
      this.visible = true
      this.adsensePending = false
      markPopupAdSeenThisSession()
      if (this.isAdsense) {
        this.$nextTick(() => this.fitAdsenseLayout())
      }
    },
    discardBanner () {
      this.stopAdsenseWatch()
      this.visible = false
      this.adsensePending = false
      this.bannerHtml = ''
    },
    waitForAdsenseFill () {
      if (this.adsenseStarted) {
        return
      }
      this.adsenseStarted = true
      this.stopAdsenseWatch()
      const root = this.$refs.bannerContent
      if (!root) {
        this.discardBanner()
        return
      }
      ensureAdsenseReady().then(() => {
        if (!this.adsensePending) {
          return
        }
        requestAdsenseFill(root)
        const ins = root.querySelector('ins.adsbygoogle')
        if (!ins) {
          this.discardBanner()
          return
        }
        const inspect = () => {
          if (ins.getAttribute('data-ad-status') === 'unfilled') {
            this.discardBanner()
            return true
          }
          if (adsenseHasVisibleCreative(ins)) {
            this.showBanner()
            return true
          }
          return false
        }
        if (inspect()) {
          return
        }
        if (window.MutationObserver) {
          this.adsenseObserver = new MutationObserver(inspect)
          this.adsenseObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
        }
        this.adsenseTimer = window.setTimeout(() => {
          if (adsenseHasVisibleCreative(ins)) {
            this.showBanner()
          } else {
            this.discardBanner()
          }
        }, ADSENSE_POPUP_WAIT_MS)
      })
    },
    closeBanner () {
      markPopupAdSeenThisSession()
      this.stopAdsenseWatch()
      this.visible = false
      this.adsensePending = false
      this.bannerHtml = ''
      this.adsenseStarted = false
    },
    fitAdsenseLayout () {
      const root = this.$refs.bannerContent
      if (!root) {
        return
      }
      const ins = root.querySelector('ins.adsbygoogle')
      const iframe = ins && ins.querySelector('iframe')
      if (!ins || !iframe) {
        return
      }
      const sync = () => {
        const rect = iframe.getBoundingClientRect()
        if (rect.height > 0) {
          ins.style.minHeight = Math.ceil(rect.height) + 'px'
        }
        if (rect.width > 0) {
          ins.style.width = Math.ceil(rect.width) + 'px'
          ins.style.maxWidth = '100%'
        }
      }
      sync()
      window.setTimeout(sync, 100)
      window.setTimeout(sync, 500)
      if (window.ResizeObserver) {
        new window.ResizeObserver(sync).observe(iframe)
      }
    }
  }
}
</script>
